import { useEffect, useRef, useState } from "react";
import { ctaHref, buildSubId } from "@/lib/affiliate";
import { API } from "@/lib/api";
import { trackBookingClick } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const LABELS = {
  hotels: "Find Hotels",
  flights: "Search Flights",
  cars: "Rent a Car",
  transfers: "Book a Transfer",
  activities: "Things to Do",
  attractions: "Attractions",
  insurance: "Travel Insurance",
};

const VARIANTS = {
  primary: "bg-primary text-primary-foreground hover:brightness-110 shadow-[0_6px_20px_rgb(255,90,0,0.28)]",
  dark: "bg-ink text-white hover:bg-ink/90",
  outline: "bg-transparent border border-foreground/20 text-foreground hover:bg-foreground hover:text-background",
  ghost: "bg-white text-ink border border-black/10 hover:border-primary hover:text-primary",
};
const SIZES = { sm: "px-4 py-2 text-xs", md: "px-6 py-3 text-sm", lg: "px-8 py-4 text-base" };
const BASE = "inline-flex items-center justify-center gap-2 rounded-full font-heading font-semibold tracking-tight transition-transform duration-200 ease-out hover:-translate-y-0.5";

// Local calendar "today" (not UTC) so a same-evening check-in is never rejected by a timezone roll.
function todayLocalISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

// Affiliate CTA. Renders an anchor to our backend /go tracker (new tab). Event CTAs carry the
// canonical event stay window automatically; EVENT-LESS hotel CTAs (city / venue / stay-area /
// evergreen guide) have no dates — Nuitée needs a stay window or it shows Invalid Date / NaN — so
// they first collect the traveler's check-in/out in a lightweight dialog (no invented dates).
export function CTAButton({
  productType = "hotels",
  ctx = {},
  label,
  variant = "primary",
  size = "md",
  className,
  testId,
  showIcon = true,
}) {
  const cls = cn(BASE, VARIANTS[variant], SIZES[size], className);
  const text = label || LABELS[productType] || "Book Now";
  const tid = testId || `cta-${productType}`;
  const icon = showIcon ? <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} /> : null;

  const needsDates = productType === "hotels" && !ctx.event;
  if (!needsDates) {
    return (
      <a
        href={ctaHref({ productType, ...ctx })}
        target="_blank"
        rel="nofollow sponsored noopener noreferrer"
        title={productType === "flights" ? "Opens Hotelstrips-branded flight booking powered by Nuitée in a new tab" : undefined}
        aria-label={productType === "flights" ? `${text} — opens Hotelstrips flight booking powered by Nuitée in a new tab` : undefined}
        onClick={() => trackBookingClick({ productType, eventSlug: ctx.event, citySlug: ctx.city,
          eventDate: ctx.eventDate, clientReference: buildSubId({ productType, ...ctx }) })}
        data-testid={tid}
        className={cls}
      >
        {productType === "flights" ? (
          <span className="inline-flex flex-col items-center leading-tight">
            <span className="inline-flex items-center gap-2">{text}{icon}</span>
            <span className="text-[10px] font-normal tracking-normal opacity-80" data-testid={`${tid}-handoff`}>Powered by Nuitée ↗</span>
          </span>
        ) : (
          <>
            {text}
            {icon}
          </>
        )}
      </a>
    );
  }
  return <HotelDateCTA ctx={ctx} productType={productType} cls={cls} text={text} tid={tid} icon={icon} />;
}

// Site-wide hotel CTAs (homepage) have no destination context; collect one from the published
// destination list so the click resolves a real city instead of a destination-less fallback.
// Returns validated, de-duplicated {slug,label} rows, or null when the body is not the expected shape.
export function normalizeDestinations(body) {
  const raw = body && typeof body === "object" ? body.destinations : undefined;
  if (!Array.isArray(raw)) return null;
  const seen = new Set();
  const out = [];
  for (const c of raw) {
    if (!c || typeof c !== "object") continue;
    const slug = typeof c.slug === "string" ? c.slug.trim() : "";
    const name = typeof c.name === "string" ? c.name.trim() : "";
    if (!slug || !name || seen.has(slug)) continue;
    seen.add(slug);
    const region = [c.state, c.country].find((v) => typeof v === "string" && v.trim());
    out.push({ slug, label: region ? `${name}, ${region.trim()}` : name });
  }
  return out;
}

const IDLE = { status: "idle", list: [] };

function useDestinations(enabled) {
  const [state, setState] = useState(IDLE);
  useEffect(() => {
    if (!enabled) { return undefined; }
    let live = true;
    setState({ status: "loading", list: [] });
    fetch(`${API}/hub/destinations`).then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => {
        if (!live) { return; }
        const list = normalizeDestinations(d);
        setState(list === null ? { status: "error", list: [] } : { status: list.length ? "ready" : "empty", list });
      })
      .catch(() => { if (live) { setState({ status: "error", list: [] }); } });
    return () => { live = false; setState(IDLE); };
  }, [enabled]);
  return state;
}

const DEST_NOTE = {
  loading: "Loading destinations…",
  empty: "No destinations are available right now.",
  error: "Destinations couldn't load. Close and try again.",
};

function DestinationSelect({ value, onChange, options, status }) {
  return (
    <label className="text-sm font-medium text-foreground block">
      Destination
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid="hotel-dates-destination"
        disabled={status !== "ready"}
        className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-primary focus:outline-none">
        <option value="">Choose a city…</option>
        {options.map((c) => (
          <option key={c.slug} value={c.slug}>{c.label}</option>
        ))}
      </select>
      {DEST_NOTE[status] && <span className="mt-1 block text-xs text-foreground/60" data-testid={`hotel-dates-destination-${status}`}>{DEST_NOTE[status]}</span>}
    </label>
  );
}

function HotelDateCTA({ ctx, productType, cls, text, tid, icon }) {
  const today = todayLocalISO();
  const [open, setOpen] = useState(false);
  const sent = useRef(false);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const needsDest = !ctx.city && !ctx.venue && !ctx.stayArea;
  const [dest, setDest] = useState("");
  const dests = useDestinations(open && needsDest);
  const destOk = dests.status === "ready" && dests.list.some((c) => c.slug === dest);
  const coherent = Boolean(checkIn && checkOut && checkIn >= today && checkOut > checkIn && (!needsDest || destOk));

  const go = () => {
    if (!coherent || sent.current) return;
    sent.current = true;
    const full = needsDest ? { ...ctx, city: dest } : ctx;
    const href = ctaHref({ productType, ...full, checkIn, checkOut });
    trackBookingClick({ productType, eventSlug: full.event, citySlug: full.city, eventDate: checkIn,
      clientReference: buildSubId({ productType, ...full }) });
    window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button type="button" data-testid={tid} className={cls} onClick={() => { sent.current = false; setOpen(true); }}>
        {text}
        {icon}
      </button>
      <DialogContent className="max-w-sm" data-testid="hotel-dates-dialog">
        <DialogHeader>
          <DialogTitle>{needsDest ? "Where and when are you staying?" : "Choose your stay dates"}</DialogTitle>
          <DialogDescription>Pick check-in and check-out to see live hotel prices.</DialogDescription>
        </DialogHeader>
        {needsDest && <DestinationSelect value={destOk ? dest : ""} onChange={setDest} options={dests.list} status={dests.status} />}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-medium text-foreground">
            Check-in
            <input
              type="date"
              min={today}
              value={checkIn}
              data-testid="hotel-dates-checkin"
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (checkOut && e.target.value >= checkOut) setCheckOut("");
              }}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </label>
          <label className="text-sm font-medium text-foreground">
            Check-out
            <input
              type="date"
              min={checkIn || today}
              value={checkOut}
              data-testid="hotel-dates-checkout"
              onChange={(e) => setCheckOut(e.target.value)}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </label>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={go}
            disabled={!coherent}
            data-testid="hotel-dates-search"
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-heading font-semibold bg-primary text-primary-foreground transition-opacity",
              !coherent && "opacity-50 cursor-not-allowed"
            )}
          >
            Search hotels
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
